import { getPropertyPriceLabel, hasPublicPrice } from '../../utils/propertyPrice';
import React, { useState, useEffect } from 'react';
import { Building2, Map, Plus, Settings, Euro, Key, User, AlignJustify, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import AdminLayout from '../../components/admin/AdminLayout';

function Dashboard() {
  const [stats, setStats] = useState({
    totalProperties: 0,
    publishedProperties: 0,
    saleProperties: 0,
    rentProperties: 0,
    zonesCount: 0,
    featuresCount: 0
  });
  const [recentProperties, setRecentProperties] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      // Get published status id
      const { data: publishedStatus } = await supabase
        .from('property_statuses')
        .select('id')
        .eq('name', 'published')
        .single();

      // Get total properties
      const { count: totalCount, error: totalError } = await supabase
        .from('properties')
        .select('*', { count: 'exact', head: true });
      
      // Get published properties
      const { count: publishedCount, error: publishedError } = await supabase
        .from('properties')
        .select('*', { count: 'exact', head: true })
        .eq('status_id', publishedStatus?.id);
      
      // Get sale properties
      const { count: saleCount, error: saleError } = await supabase
        .from('properties')
        .select('*', { count: 'exact', head: true })
        .eq('type', 'vendita');
      
      // Get rent properties
      const { count: rentCount, error: rentError } = await supabase
        .from('properties')
        .select('*', { count: 'exact', head: true })
        .eq('type', 'affitto');
      
      // Get zones count
      const { count: zonesCount, error: zonesError } = await supabase
        .from('zones')
        .select('*', { count: 'exact', head: true });
      
      // Get features count
      const { count: featuresCount, error: featuresError } = await supabase
        .from('features')
        .select('*', { count: 'exact', head: true });
      
      if (totalError || publishedError || saleError || rentError || zonesError || featuresError) {
        console.error('Error fetching stats');
      } else {
        setStats({
          totalProperties: totalCount || 0,
          publishedProperties: publishedCount || 0,
          saleProperties: saleCount || 0,
          rentProperties: rentCount || 0,
          zonesCount: zonesCount || 0,
          featuresCount: featuresCount || 0
        });
      }
      
      // Get recent properties
      const { data: recentData, error: recentError } = await supabase
        .from('properties')
        .select(`
          *,
          zones(name),
          provinces(name),
          property_statuses(name)
        `)
        .order('created_at', { ascending: false })
        .limit(5);
      
      if (recentError) {
        console.error('Error fetching recent properties:', recentError);
      } else {
        setRecentProperties(recentData || []);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout title="Dashboard">
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
          <p className="mt-4 text-lg text-gray-600">Caricamento dati...</p>
        </div>
      ) : (
        <>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Immobili Totali</h3>
                <Building2 className="w-6 h-6 text-blue-600" />
              </div>
              <p className="text-3xl font-bold">{stats.totalProperties}</p>
              <p className="text-sm text-gray-500 mt-2">Pubblicati: {stats.publishedProperties}</p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">In Vendita</h3>
                <Euro className="w-6 h-6 text-green-600" />
              </div>
              <p className="text-3xl font-bold">{stats.saleProperties}</p>
              <p className="text-sm text-gray-500 mt-2">{Math.round((stats.saleProperties / stats.totalProperties) * 100) || 0}% del totale</p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">In Affitto</h3>
                <Key className="w-6 h-6 text-orange-600" />
              </div>
              <p className="text-3xl font-bold">{stats.rentProperties}</p>
              <p className="text-sm text-gray-500 mt-2">{Math.round((stats.rentProperties / stats.totalProperties) * 100) || 0}% del totale</p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Zone</h3>
                <Map className="w-6 h-6 text-purple-600" />
              </div>
              <p className="text-3xl font-bold">{stats.zonesCount}</p>
              <p className="text-sm text-gray-500 mt-2">Dotazioni: {stats.featuresCount}</p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            <div className="md:col-span-2">
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-xl font-semibold mb-6">Ultimi Immobili</h2>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-4">Titolo</th>
                        <th className="text-left py-3 px-4">Tipo</th>
                        <th className="text-left py-3 px-4">Prezzo</th>
                        <th className="text-left py-3 px-4">Stato</th>
                        <th className="text-left py-3 px-4">Azioni</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentProperties.length === 0 ? (
                        <tr className="text-gray-500">
                          <td colSpan={5} className="text-center py-4">
                            Nessun immobile trovato
                          </td>
                        </tr>
                      ) : (
                        recentProperties.map((property: any) => (
                          <tr key={property.id} className="border-t">
                            <td className="py-3 px-4">{property.title}</td>
                            <td className="py-3 px-4">
                              <span className={`px-2 py-1 rounded-full text-sm ${
                                property.type === 'vendita'
                                  ? 'bg-blue-100 text-blue-800'
                                  : 'bg-green-100 text-green-800'
                              }`}>
                                {property.type === 'vendita' ? 'Vendita' : 'Affitto'}
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              {getPropertyPriceLabel(property)}
                              {hasPublicPrice(property) && property.type === 'affitto' && '/mese'}
                            </td>
                            <td className="py-3 px-4">
                              <span className={`px-2 py-1 rounded-full text-sm ${
                                property.property_statuses?.name === 'published'
                                  ? 'bg-green-100 text-green-800'
                                  : property.property_statuses?.name === 'draft'
                                  ? 'bg-gray-100 text-gray-800'
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {property.property_statuses?.name === 'published' ? 'Pubblicato' : 
                                 property.property_statuses?.name === 'draft' ? 'Bozza' : 'Archiviato'}
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex space-x-2">
                                <a
                                  href={`/admin/properties/${property.id}/edit`}
                                  className="text-blue-600 hover:text-blue-700"
                                  title="Modifica"
                                >
                                  <Settings className="w-5 h-5" />
                                </a>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                {recentProperties.length > 0 && (
                  <div className="mt-4 text-right">
                    <Link
                      to="/admin/properties"
                      className="text-primary hover:text-primary-dark flex items-center justify-end"
                    >
                      Vedi tutti gli immobili
                      <ArrowRight className="ml-1 h-4 w-4" />
                    </Link>
                  </div>
                )}
              </div>
            </div>

            <div>
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-xl font-semibold mb-6">Azioni Rapide</h2>
                <div className="space-y-4">
                  <a
                    href="/admin/properties/new"
                    className="flex items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <Plus className="w-5 h-5 mr-3 text-blue-600" />
                    <span>Aggiungi Immobile</span>
                  </a>
                  <Link
                    to="/admin/features"
                    className="flex items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <AlignJustify className="w-5 h-5 mr-3 text-purple-600" />
                    <span>Gestisci Dotazioni</span>
                  </Link>
                  <Link
                    to="/admin/zones"
                    className="flex items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <Map className="w-5 h-5 mr-3 text-purple-600" />
                    <span>Gestisci Zone</span>
                  </Link>
                  <Link
                    to="/admin/settings"
                    className="flex items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <Settings className="w-5 h-5 mr-3 text-gray-600" />
                    <span>Impostazioni</span>
                  </Link>
                </div>
              </div>
              
              <div className="bg-white rounded-lg shadow-sm p-6 mt-6">
                <h2 className="text-xl font-semibold mb-4">Informazioni Utili</h2>
                <div className="space-y-3">
                  <div>
                    <h3 className="font-medium text-gray-700">Navigazione</h3>
                    <p className="text-gray-600 text-sm">Usa il menu laterale per navigare tra le diverse sezioni dell'area amministrativa.</p>
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-700">Gestione Immobili</h3>
                    <p className="text-gray-600 text-sm">Aggiungi, modifica ed elimina gli immobili dalla sezione "Immobili".</p>
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-700">Supporto</h3>
                    <p className="text-gray-600 text-sm">Per assistenza, contatta l'amministratore di sistema.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </AdminLayout>
  );
}

export default Dashboard;